import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Proptypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import ErrorModal from "@meetyoo/components/src/components/Modal/ErrorModal";
import LinearProgress from "@material-ui/core/LinearProgress";
import TextField from "@material-ui/core/TextField";

import { sendingDocumentNumbers, sendingDocumentNumberOnhold, resetFailures } from "../../../actions/billing";

import { defaultPasscodeTheme } from "../../../constants/passCodeUtils";

const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  centerer: {
    display: "flex",
    justifyContent: "center",
  },
  button: {
    width: "175px",
    margin: theme.spacing(2, 1, 1, 1),
  },
}));

const BillingDocumentSendingEventComponent = ({
  documentNumbers,
  translate,
  sendingDocumentNumbersFn,
  setDocumentNumberOnholdFn,
  dispatchComment,
  navCompany,
  failureRequests,
  resetErrorFn,
}) => {
  const classes = useStyles();
  const [documentNumbersCount, setDocumentNumbersCount] = useState(0);
  const [makeRequest, setMakeRequest] = useState(false);
  const [onholdRequest, setOnholdRequest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (makeRequest && documentNumbersCount < documentNumbers.length)
        await sendingDocumentNumbersFn(navCompany, documentNumbers[documentNumbersCount], testEmail !== "" ? testEmail : null);
      if (onholdRequest && documentNumbersCount < documentNumbers.length)
        await setDocumentNumberOnholdFn(navCompany, documentNumbers[documentNumbersCount]);
      if (makeRequest || onholdRequest) setDocumentNumbersCount(documentNumbersCount + 1);
    };
    if (documentNumbersCount === documentNumbers.length || failureRequests[0] === documentNumbers[0]) {
      setMakeRequest(false);
      setOnholdRequest(false);
    }
    if (failureRequests[0] === documentNumbers[0] && failureRequests.length < 0) {
      setErrorMessage(
        "Es scheint als könnten sie den Billing-Service nicht erreichen. Oder es gibt einen Fehler bei der ersten Rechnung"
      );
      return;
    }
    fetchData();
  }, [
    makeRequest,
    onholdRequest,
    documentNumbersCount,
    failureRequests,
    setDocumentNumbersCount,
    documentNumbers,
    sendingDocumentNumbersFn,
    setDocumentNumberOnholdFn,
    testEmail,
    navCompany,
  ]);

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  return [
    <div key="buttonbar-wrapper" className={classes.centerer}>
      <div key="buttonbar">
        <TextField
          key="send-document-test-mail"
          label="Test E-Mail"
          className=""
          value={testEmail}
          onChange={event => setTestEmail(event.target.value)}
          margin="normal"
          disabled={makeRequest}
        />
        <Button
          key="send-document-button"
          variant="contained"
          className={classes.button}
          onClick={() => {
            if (dispatchComment === "") {
              setDocumentNumbersCount(0);
              resetErrorFn();
              setMakeRequest(true);
            } else {
              setOpen(true);
            }
          }}
          disabled={onholdRequest || makeRequest}
        >
          Send Documents
        </Button>
        <Dialog
          key="send-document-with-dispatchComment-dialog"
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Belegversandposten versenden:</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Beim versenden wird der gesetzte &quot;{dispatchComment}&quot; Status (eventuell) aufgehoben.
            </DialogContentText>
            <DialogContentText id="alert-dialog-question">
              Möchten Sie die {documentNumbers.length} Belegversandposten mit der Versandbemerkung &quot;
              {dispatchComment}&quot; wirklich versenden?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button key="dialog-cancel-button" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              key="dialog-execuite-button"
              autoFocus
              onClick={() => {
                setDocumentNumbersCount(0);
                resetErrorFn();
                setMakeRequest(true);
                handleClose();
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {dispatchComment !== "ONHOLD" && (
          <Button
            key="set-onhold-button"
            variant="contained"
            className={classes.button}
            onClick={() => {
              setDocumentNumbersCount(0);
              resetErrorFn();
              setOnholdRequest(true);
            }}
            disabled={onholdRequest || makeRequest}
          >
            {translate.onHold}
          </Button>
        )}
        <div key="buttonbar-linebreak-1" className={classes.linebreak} />
        {documentNumbersCount < documentNumbers.length && (makeRequest || onholdRequest) && (
          <div key="progress-bar">
            <div key="progress-bar-message">Sending Document Number: {documentNumbers[documentNumbersCount]}</div>
            <LinearProgress variant="determinate" value={(documentNumbersCount / documentNumbers.length) * 100} />
            <Button
              key="stop-button"
              variant="contained"
              className={classes.button}
              onClick={() => {
                setMakeRequest(false);
                setOnholdRequest(false);
              }}
            >
              STOP!
            </Button>
          </div>
        )}
        {failureRequests.map(failure => (
          <div key="progress-error">Problem on Sending Document Number: {failure}</div>
        ))}
        {documentNumbersCount === documentNumbers.length && <div key="progress-success">All Jobs Done!</div>}
      </div>
    </div>,
    <ErrorModal
      key="error"
      handleCloseErrorModal={() => setErrorMessage("")}
      openErrorModal={errorMessage !== ""}
      errorMessage={errorMessage}
    />,
  ];
};

BillingDocumentSendingEventComponent.proptypes = {
  documentNumbers: Proptypes.array.isRequired,
  errorMessage: Proptypes.string.isRequired,
  sendingDocumentNumbersFn: Proptypes.func.isRequired,
  setDocumentNumberOnholdFn: Proptypes.func.isRequired,
  dispatchComment: Proptypes.string.isRequired,
  navCompany: Proptypes.object.isRequired,
  translate: Proptypes.object.isRequired,
  failureRequests: Proptypes.array.isRequired,
  resetErrorFn: Proptypes.func.isRequired,
};

const mapStateToProps = state => ({
  documentNumbers: state.billing.documentNumbers,
  navCompany: state.billing.navCompany,
  dispatchComment: state.billing.documentFilter.dispatchComment,
  translate: state.ui.translate,
  failureRequests: state.billing.failureRequests,
});

const mapDispatchToProps = dispatch => ({
  sendingDocumentNumbersFn: async (navCompany, id, sendToTestAddress) => {
    await dispatch(sendingDocumentNumbers(navCompany.key, id, sendToTestAddress));
  },
  setDocumentNumberOnholdFn: async (navCompany, id) => {
    await dispatch(sendingDocumentNumberOnhold(navCompany.key, id));
  },
  resetErrorFn: () => dispatch(resetFailures()),
});

const BillingDocumentSendingEvent = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingDocumentSendingEventComponent);

export default BillingDocumentSendingEvent;
